import classNames from "classnames";
import React, { useRef } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    isOverflowingX,
    useTruncationTooltip,
} from "@design-system/Tooltip/useTruncationTooltip";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { useBreakableText } from "@hooks/useBreakableText";
import { useIdCopy } from "@hooks/useIdCopy";
import { useShowCounterparty } from "@hooks/useShowCounterparty";
import { Amount } from "@design-system/Amount/Amount";
import { TransactionStatus } from "../TransactionStatus";
import { CashItemIndicator } from "../views/CashItemIndicator";
import styles from "./TransactionDescriptionColumnContent.module.scss";

interface Props {
    transaction: TransactionListItem;
    className?: string;
}

export const TransactionDescriptionColumnContent: React.FC<Props> = React.memo(
    ({ transaction, className }) => {
        const isSplit = transaction.splitParentId !== null;
        const { getCopyProps } = useIdCopy({ label: "Transaction" });

        const description =
            transaction.displayDescription ?? transaction.bankDescription;
        const breakableDescription = useBreakableText(description);

        const showCounterparty = useShowCounterparty();
        const counterpartyToShow = showCounterparty(transaction.counterparty)
            ? transaction.counterparty
            : null;

        const breakableTextContainer = useRef<HTMLSpanElement>(null);
        const hasDetailsBeyondText = isSplit || !!counterpartyToShow;
        const tooltipOpenProps = useTruncationTooltip({
            ref: breakableTextContainer,
            isTruncated: (element) =>
                hasDetailsBeyondText || isOverflowingX(element),
        });
        const showFullDescription = description !== transaction.bankDescription;

        return (
            <div className={classNames(styles.content, className)}>
                <Tooltip
                    {...tooltipOpenProps}
                    content={
                        <>
                            {showFullDescription && (
                                <>
                                    <span className="label">Description</span>
                                    <p>{description}</p>
                                </>
                            )}
                            {isSplit ? (
                                <>
                                    <span className="label">
                                        Bank Description
                                    </span>
                                    <p>{transaction.bankDescription} </p>
                                    <span className="label">
                                        Original Amount
                                    </span>
                                    <p className="mb-0">
                                        <Amount
                                            amount={transaction.originalAmount}
                                            showNegative
                                        />
                                    </p>
                                </>
                            ) : (
                                <>
                                    {counterpartyToShow && (
                                        <>
                                            <span className="label">
                                                Counterparty
                                            </span>
                                            <p>{counterpartyToShow.name}</p>
                                        </>
                                    )}
                                    <span className="label">
                                        Bank description
                                    </span>
                                    <p className="mb-0">
                                        {transaction.bankDescription}
                                    </p>
                                </>
                            )}
                        </>
                    }
                >
                    <span
                        className={styles.descriptionText}
                        data-testid="transaction-list-item-description"
                        ref={breakableTextContainer}
                        {...getCopyProps(transaction.id)}
                    >
                        {breakableDescription}
                    </span>
                </Tooltip>
                <CashItemIndicator transaction={transaction} />
                <TransactionStatus transaction={transaction} />
            </div>
        );
    },
    (prev, next) =>
        prev.transaction === next.transaction &&
        prev.className === next.className,
);
TransactionDescriptionColumnContent.displayName =
    "TransactionDescriptionColumnContent";
